import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
import moment from 'moment';

@Component({
    selector: 'date-span',
    templateUrl: './date-span.component.html',
    styleUrls: ['./date-span.component.scss'],
    standalone: false
})
export class DateSpanComponent implements OnInit, OnChanges {

    /** The name to associate with the date span in the UI. Typically the verb of the name. (ex: Modified, Sold, Received) */
    @Input() verb: string | null = null

    /** The sessionStorage key to use when loading the default value. */
    @Input() default: string | null = null

    /** The minimum choice available, (i.e. 'today') the rest before, like Anytime, should not show. */
    @Input() min: string | null = null

    /** The maximum choice available, (i.e. 'quarter') the rest after should not show. */
    @Input() max: string | null = null

    /** Listen for user choices in the date span drop down menu. */
    @Output() chosen = new EventEmitter<Object>()

    /** Custom date span form */
    public form: FormGroup | null = null

    /** */
    public selection: string | null = null

    /** The available options to choose from for the built-in date spans. */
    protected choices = [
        {key: '', phrase: 'Anytime (default)'},
        {key: 'today', phrase: 'Today'},
        {key: 'week', phrase: 'within a week'},
        {key: 'twoweeks', phrase: 'within two weeks'},
        {key: 'month', phrase: 'within a month'},
        {key: 'quarter', phrase: 'within three months'},
        {key: 'sixmonths', phrase: 'within six months'},
        {key: 'year', phrase: 'within a year'}
    ]

    /**
     * 
     */
    constructor(fb: FormBuilder) {
        this.form = fb.group({
            from: new FormControl<Date>(null, []),
            to: new FormControl<Date>(null, [])
        })
    }

    ngOnInit() {
        this.selection = `${this.verb} ${this.choices[0].phrase}`
        this.applyMin()
        this.applyMax()
        this.loadDefault()
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }

        if (changes.verb) {
            if (!this.selection) {
                this.selection = `${this.verb} ${this.choices[0].phrase}`
            }

            this.selection.replace(changes.verb.previousValue, changes.verb.currentValue)
        }

        if (changes.default) {
            this.loadDefault()
        }
    }

    private applyMin = () => {
        if (!this.min || (this.min.length <= 0)) {
            return
        }

        const index = this.choices.findIndex((item) => item.key === this.min.toLocaleLowerCase())
        if (index < 0) {
            return
        }

        this.choices = this.choices.slice(index)
        this.choices[0].phrase += ' (default)'
    }

    private applyMax = () => {
        if (!this.max || (this.max.length <= 0)) {
            return
        }

        const index = this.choices.findIndex((item) => item.key === this.max.toLocaleLowerCase())
        if (index < 0) {
            return
        }

        this.choices = this.choices.slice(0, index+1)
    }

    /**
     * Check sessionStorage for the default value.
     */
    private loadDefault = () => {
        if (!this.default || (this.default.length <= 0)) {
            return
        }

        const saved = sessionStorage.getItem(this.default)
        if (!saved || (saved.length <= 0)) {
            return
        }

        if (saved.startsWith('custom')) {
            const items = saved.split(';')
            if (!items || (items.length !== 3)) {
                return
            }

            this.form.get('from').setValue(items[1], {emitEvent: false})
            this.form.get('to').setValue(items[2], {emitEvent: false})  
            
            this.selection = `${moment(items[1]).format('MMM Do, YY')} - ${moment(items[2]).format('MMM Do, YY')}`
            return
        }

        const choice = this.choices.find((item) => item.key === saved)
        if (!choice) {
            return
        }

        this.selection = `${this.verb} ${choice.phrase}`
    }
    
    /**
     * 
     * @param e 
     * @param index
     */
    public clicked = (e: Event, index: number) => {
        if (!e) {
            return
        }

        e.preventDefault()

        if (index > (this.choices.length-1)) {
            return
        }

        const key = (index >= 0) ? this.choices[index].key : 'custom'
        if ((key === undefined) || (key === null) || !this.chosen) {
            return
        }

        if (key === 'custom') {
            const from = this.form.get('from').value
            const to = this.form.get('to').value
            if (!from || !to) {
                return
            }

            this.chosen.next({key: key, from: from, to: to})
            this.selection = `${moment(from).format('MMM Do, YY')} - ${moment(to).format('MMM Do, YY')}`
            this.hide()
            return
        }

        this.chosen.next({key: key, from: null, to: null})
        const phrase = this.choices[index].phrase
        this.selection = `${this.verb} ${phrase}`
    }

    /**
     * Hide the dropdown when needed (custom dates).
     */
    private hide = () => {
        document.body.click()
    }
}

